import { observable, reaction, computed, action, makeObservable, values, runInAction, autorun, toJS } from 'mobx';
import { parseShape, wrapPromise } from './utils';
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import {
    customerInfoByPhoneQuery,
    GET_ALL_ORDERS,
    GET_CUSTOMER_BUYS,
    GET_CUSTOMER_INFO_BY_PHONE,
    GET_CUSTOMER_PRICE,
    GET_CUSTOMER_PURCHASES,
    GET_USER_BY_PHONE_IF_AUTHENTICATED,
    UPDATE_CUSTOMER,
    UPDATE_PASSWORD,
    UPDATE_USER
} from 'api/graphql/user';
import { useEffect } from 'react';
import PurchasedItem from './PurchasedItem';
import PurchasedDigital from './PurchasedDigital';
import { createOrder  } from './Order';
import Ticket from './Order/Ticket';
import Order from './Order/Order';
import { GET_ONE_ORDER } from 'api/graphql/orders';

class UserStore {

    user = {
        username: null,
        firstName: null,
        middleName: null,
        lastName: null,
        email: null,
        birthday: null,
        balance: null,
        skype: null,
        sex: null,
        ask: null,
        isStaff: null
    };
    address = {
        id: null,
        country: null,
        region: null,
        city: null,
        index: null,
        address: null
    };
    customerInfo = null;
    isAuthenticated = window.user !== 'AnonymousUser';
    loading = false;
    username = window.user;
    orders = [];
    purchases = [];

    constructor(model) {
        this.model = model;
        makeObservable(this, {
            model: false,
            loading: observable,
            user: observable,
            customerInfo: observable,
            isAuthenticated: observable,
            setCustomerInfo: action.bound,
            customerName: computed,
            orders: observable,
            pastOrders: computed,
            activeOrders: computed,
            purchases: observable,
            groupedPurchases: computed,
            authenticate: action,
            removeOrder: action.bound
        });

        autorun(() => {
            if (this.isAuthenticated) {
                this.updateUserInfo();
            }
        })
    }

    get customerName() {
        return this.customerInfo ? this.customerInfo.name : null;
    }

    setCustomerInfo(info) {
        this.customerInfo = info;
    }

    get userData() {
        if (this.customerInfo && this.username ) {
            return { read: () => this.user }
        }
        return wrapPromise(this.updateUserInfo());
    }

    get userOrdersData() {
        return wrapPromise(this.getUserOrders());
    }

    userOrderData = (id) => {
        const promise = Promise.all([this.updateUserInfo(), this.getUserOrder(id)]);
        return wrapPromise(promise);
    }

    get userPurchaseData() {
        return wrapPromise(this.getUserPurchases());
    }

    updateUserInfo = () => {
        //ask for data only if window.user is set
        if (this.isAuthenticated) {
            if (!isValidPhoneNumber(this.username)) {
                console.log('this is admin account');
                runInAction(() => {
                    this.user = {
                        username: this.username,
                        nationalPhoneNumber: '',
                        firstName: '',
                        middleName: '',
                        lastName: '',
                        email: '',
                        birthday: '',
                        balance: 0,
                        skype: '',
                        sex: 'M',
                        ask: '',
                        isStaff: true
                    }
                    this.address = null;
                });
                return Promise.resolve().then(() => this.user);
            }
            return this.model.apollo.query({
                query: GET_USER_BY_PHONE_IF_AUTHENTICATED,
                variables: {username: this.username}
            }).then(({data}) => {
                const parsed = parseShape(data)['userUsername'][0];
                const parsedPhoneNumber = parsePhoneNumber(this.username)
                runInAction(() => {
                    this.user = {
                        username: this.username,
                        nationalPhoneNumber: parsedPhoneNumber.nationalNumber,
                        firstName: parsed.firstName ?? '',
                        middleName: parsed.customer.middleName ?? '',
                        lastName: parsed.lastName ?? '',
                        email: parsed.email ?? '',
                        birthday: parsed.customer.birthday ?? '',
                        balance: parsed.customer.balance ?? 0,
                        skype: parsed.customer.skype ?? '',
                        sex: parsed.customer.sex ?? 'M',
                        ask: parsed.customer.ask ?? '',
                        isStaff: parsed.isStaff
                    }
                    const address = parsed.customer?.adres?.[0];
                    this.address = address ? {
                        id: address.id,
                        country: address.country,
                        region: address.region,
                        city: address.city,
                        index: address.index,
                        address: address.address
                    } : null;
                });
                this.setCustomerInfo({ ...parsed, country: parsedPhoneNumber.country });
                return this.user;
            })
        } else {
            return Promise.reject();
        }
    }

    getUserOrders = () => {
        if (this.isAuthenticated) {
            return this._getUserOrdersByCursor()
                .then(() => this.model.specialist.getSpecialists())
        } else {
            return Promise.reject();
        }
    }

    getUserOrder = (id) => {
        if (this.isAuthenticated) {
            return this._getUserOrder(id);
        } else {
            return Promise.reject();
        }
    }

    _getUserOrdersByCursor = (cursor = null) => {
        return this.model.apollo.query({
            query: GET_ALL_ORDERS,
            variables: {username: this.username, cursor}
        }).then((response) => {
            const parsed = parseShape(response.data)['allOrders'];
            runInAction(() => {
                parsed.forEach(json => this.updateOrderFromServer(json));
            });
            const { hasNextPage, endCursor } = response.data['allOrders']['pageInfo'];
            if (hasNextPage) {
                return this._getUserOrdersByCursor(endCursor);
            }
            runInAction(() => {
                this.orders = this.orders.sort((o1, o2) =>
                    ((o1.isOrderPaid && o2.isOrderPaid)) ?
                        new Date(o1.earliestDate) - new Date(o2.earliestDate) :
                    (!o1.isOrderPaid && !o2.isOrderPaid) ?
                        new Date(o1.order.createdAt) - new Date(o2.order.createdAt) :
                    o1.isOrderPaid && !o2.isOrderPaid ? 1 : -1);

            })
        })
    }

    _getUserOrder = (id = null) => {
        return this.model.apollo.query({
            query: GET_ONE_ORDER,
            variables: { id },
            fetchPolicy: "no-cache"
        }).then((response) => {
            const parsed = parseShape(response.data)['allOrders'];
            runInAction(() => {
                parsed.forEach(json => this.updateOrderFromServer(json));
                this.orders = this.orders.sort((o1, o2) =>
                    ((o1.isOrderPaid && o2.isOrderPaid)) ?
                        new Date(o1.earliestDate) - new Date(o2.earliestDate) :
                        (!o1.isOrderPaid && !o2.isOrderPaid) ?
                            new Date(o1.order.createdAt) - new Date(o2.order.createdAt) :
                            o1.isOrderPaid && !o2.isOrderPaid ? 1 : -1);

            });
            return this.orders.find(o => o.id === id);
        })
    }


    getUserPurchases = () => {
        if (this.isAuthenticated) {
            const getUserItems = this.model.apollo.query( {
                query: GET_CUSTOMER_BUYS,
                variables: {
                    customer_User_Username: this.username,
                }
            });
            const getUserDigital = this.model.apollo.query( {
                query: GET_CUSTOMER_PURCHASES,
                variables: {
                    customer_User_Username: this.username,
                }
            });
            return Promise.all( [
                getUserItems,
                getUserDigital
            ] ).then( ( response ) => {
                const parsedBuys = parseShape(response?.[0]?.['data']?.['allBuys']);
                const parsedPurchases = parseShape(response?.[1]?.['data']?.['allPurchases']);
                runInAction(() => {
                    parsedBuys.forEach(json => {
                        this.updatePurchasedItemsFromServer(json)
                    });
                    parsedPurchases.forEach(json => {
                        this.updatePurchasedDigitalsFromServer(json)
                    });
                });
                return this.purchases;
            } ).catch( ( err ) => {
                console.error(err);
                return `Произошла ошибка ${ err }`
            } );
        } else {
            return Promise.reject('Пользователь не авторизован')
        }
    }

    getUserDigital = () => {
        if (this.isAuthenticated) {
            return this.model.apollo.query( {
                query: GET_CUSTOMER_PURCHASES,
                variables: {
                    customer_User_Username: this.username,
                }
            }).then( ( response ) => {
                const parsedPurchases = parseShape(response?.['data']?.['allPurchases']);
                runInAction(() => {
                    parsedPurchases.forEach(json => {
                        this.updatePurchasedDigitalsFromServer(json)
                    });
                });
                return this.purchases;
            } ).catch( ( err ) => {
                console.error(err);
                return `Произошла ошибка ${ err }`
            } );
        } else {
            return Promise.reject('Пользователь не авторизован')
        }
    }

    updatePurchasedItemsFromServer(json) {
        const purchasedItem = this.purchases.find(item => item.id === json.id)
        if (!purchasedItem) {
            const newPurchasedItem = new PurchasedItem(this, json);
            this.purchases.push(newPurchasedItem);
        } else {
            purchasedItem.updateFromJson(json);
        }
    }

    updatePurchasedDigitalsFromServer(json) {
        const purchasedDigital = this.purchases.find(item => item.id === json.id)
        if (!purchasedDigital) {
            const newPurchasedDigital = new PurchasedDigital(this, json);
            this.purchases.push(newPurchasedDigital);
        } else {
            purchasedDigital.updateFromJson(json);
        }
    }

    updateOrderFromServer(json) {
        const isTicket = json.ticketSet?.[0]?.id;
        if (!json.event) return;
        let order = this.orders.find(item => isTicket ? item.id === json.ticketSet?.[0]?.id : item.id === json.id)
        let entity;
        if (!order) {
            entity = createOrder(json);
            this.orders.push(entity);
        } else {
            order.updateFromJson(json)
            entity = order;
        }
        return entity;
    }

    updateUserPassword = (password) => {
        if (this.isAuthenticated) {
            return this.model.apollo.query({
                query: UPDATE_PASSWORD,
                variables: {username: this.username, password}
            }).then((response) => {
                const userId = response['data']?.['updateUser']?.['user']?.['id'];
                return { userId };
            }).catch((err) => {
                return err;
            })
        } else {
            return new Promise.reject('Пользователь не авторизован')
        }
    }

    updateUser = ({ email, firstName, lastName, skype, birthday, sex, ask }) => {
        if (this.isAuthenticated) {
            runInAction(() => {
                this.user = {
                    ...this.user,
                    firstName: firstName,
                    lastName: lastName,
                    birthday: birthday,
                    email: email,
                    skype: skype,
                    sex: sex,
                    ask: ask
                }
            });
            const updateUserPromise = this.model.apollo.query( {
                query: UPDATE_USER,
                variables: {
                    username: this.username,
                    email,
                    firstName,
                    lastName
                }
            });

            const updateCustomerPromise = this.model.apollo.query( {
                query: UPDATE_CUSTOMER,
                variables: {
                    username: this.username,
                    skype,
                    birthday,
                    sex,
                    ask
                }
            })

            return Promise.all( [
                updateUserPromise,
                updateCustomerPromise
            ] ).then( ( response ) => {
                const user = response?.[0]?.['data']?.['updateUser']?.['user'];
                const customer = response?.[1]?.['data']?.['updateCustomer']?.['customer'];
                const userId = user?.['id'];
                const customerId = customer?.['id'];
                runInAction(() => {
                    if (user) {
                        this.user = {
                            ...this.user,
                            firstName: user.firstName,
                            middleName: customer.middleName,
                            lastName: user.lastName,
                            email: user.email,
                            skype: customer.skype,
                            sex: customer.sex,
                            ask: customer.ask
                        }
                    }
                });
                if ( userId && customerId ) {
                    return { userId, customerId }
                } else {
                    return 'Произошла неизвестная ошибка';
                }
            } ).catch( ( err ) => {
                console.log('catch');
                return `Произошла ошибка ${ err }`
            } );
        } else {
            return new Promise.reject('Пользователь не авторизован')
        }
    }

    get pastOrders() {
        return this.orders.filter(order => order.isOrderHistory).sort((o1, o2) => new Date(o2.latestDate) - new Date(o1.latestDate));
    }

    get activeOrders() {
        return this.orders.filter(order => order.isOrderActive);
    }

    getCustomerInfoByPhone(username) {
        return this.model.apollo.query({
            query: GET_CUSTOMER_INFO_BY_PHONE,
            variables: {username}
        }).then((response) => {
            return parseShape(response)['data']['customerInfo'][0];
        }).catch((err) => {
            console.error(err);
        })
    }

    authenticate = ({username}, cb = () => {}) => {
        this.isAuthenticated = true;
        this.username = username;
        this.updateUserInfo();
        cb();
    };

    get groupedPurchases() {
        const map = new Map()
        this.purchases.filter(purchase => purchase.isPaid || purchase.canBePaid)
            .forEach((purchase) => {
                map.has(purchase.createdAt)
                    ? map.get(purchase.createdAt).push(purchase)
                    : map.set(purchase.createdAt, [purchase])
            });
        return new Map([...map.entries()].sort(([_data1, p1], [_data2, p2]) => {
            return new Date(p2[0].data.createdAt).getTime() - new Date(p1[0].data.createdAt).getTime()
        }));
    }

    getDigital(name) {
        const findDigital = (purchases) => {
            return purchases.find(purchase => {
                return purchase?.name === name && purchase?.data?.payment?.status === 'A_1'
            });
        }
        if (this.purchases.length === 0) {
            return this.getUserDigital().then((purchases) => {
                return findDigital(purchases);
            })
        } else {
            return Promise.resolve(findDigital(this.purchases));
        }
    }

    getDigitalByUrl(url) {
        const findDigital = (purchases) => {
            return purchases.find(purchase => {
                return purchase?.digital?.url === url && purchase?.data?.payment?.status === 'A_1'
            });
        }
        if (this.purchases.length === 0) {
            return this.getUserDigital().then((purchases) => {
                return findDigital(purchases);
            })
        } else {
            return Promise.resolve(findDigital(this.purchases));
        }
    }

    removeOrder(id) {
        this.orders = this.orders.filter(order => order.id !== id)
    }

    // setPurchasePaid(pk) {
    //     runInAction(() => {
    //         const purchase = this.purchases.find(purchase => purchase.data?.pk === pk);
    //         const index = this.purchases.findIndex(purchase => purchase.data?.pk === pk);
    //         purchase.setPaid();
    //         this.purchases.splice(index, 1, purchase);
    //     })
    // }
}

export default UserStore;
